// src/utilities/geoPointUtils.js
//
// The Prospect Call Leads location filter carries points as hex-encoded
// EWKB — the exact text form PostGIS/GeoAlchemy2 uses for a `geography`
// column (what you get back from `SELECT lat_long_geo FROM leads` with no
// cast, e.g. "0101000020E6100000EC51B81E85C55240295C8FC2F5283340"), not
// separate lat/lng fields or WKT. This lets the backend hand the value
// straight to WKBElement(geo_point) with no reformatting.
//
// EWKB point layout (little-endian / NDR, SRID 4326 / WGS84):
//   byte 0      : 0x01 (byte order = little-endian)
//   bytes 1-4   : geometry type (1 = Point) OR'd with 0x20000000 (SRID flag)
//   bytes 5-8   : SRID (4326)
//   bytes 9-16  : X (longitude) as a little-endian float64
//   bytes 17-24 : Y (latitude) as a little-endian float64

const POINT_TYPE_WITH_SRID = 0x20000001;
const WGS84_SRID = 4326;

export function toHexEwkbPoint(lat, lng) {
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null;

  const buf = new ArrayBuffer(25);
  const view = new DataView(buf);
  view.setUint8(0, 1);
  view.setUint32(1, POINT_TYPE_WITH_SRID, true);
  view.setUint32(5, WGS84_SRID, true);
  view.setFloat64(9, lngNum, true);
  view.setFloat64(17, latNum, true);

  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

/** Parses a hex EWKB Point string into { lat, lng }, or null if malformed / not a Point. */
export function parseHexEwkbPoint(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const clean = hex.trim();
  if (!/^[0-9a-fA-F]+$/.test(clean) || clean.length % 2 !== 0 || clean.length < 42) return null;

  try {
    const byteMatches = clean.match(/.{2}/g);
    const bytes = new Uint8Array(byteMatches.map((b) => parseInt(b, 16)));
    const view = new DataView(bytes.buffer);

    const littleEndian = view.getUint8(0) === 1;
    const typeAndFlags = view.getUint32(1, littleEndian);
    const geomType = typeAndFlags & 0xff;
    if (geomType !== 1) return null; // not a Point

    const hasSrid = (typeAndFlags & 0x20000000) !== 0;
    let offset = 5;
    if (hasSrid) offset += 4;
    if (bytes.length < offset + 16) return null;

    const lng = view.getFloat64(offset, littleEndian);
    const lat = view.getFloat64(offset + 8, littleEndian);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  } catch {
    return null;
  }
}
